// "@" tagging in notes (Stephanie 2026-10-21) — the pure text rules, shared by
// the note box and the offline gate. No app imports.

export interface Person { id: string; name: string }

/** The "@query" being typed right before the caret, or null. "@" must start
 *  the text or follow a space/new line; the query is letters, spaces, dots,
 *  hyphens and apostrophes (so "@Mary Jo" works), at most 40 characters. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const q = before.slice(at + 1);
  if (q.length > 40 || /\n/.test(q) || !/^[\p{L}\s.'-]*$/u.test(q)) return null;
  return { start: at, query: q };
}

/** Pure: staff whose name matches what's typed (any word starting with it,
 *  or the whole name), A to Z, at most 8. */
export function matchPeople(people: readonly Person[], query: string): Person[] {
  const q = query.trim().toLowerCase();
  const hit = (p: Person) => !q || p.name.toLowerCase().startsWith(q) || p.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q));
  return people.filter(hit).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 8);
}

/** Pure: replace the "@query" with "@Full Name " and put the caret after it. */
export function insertMention(text: string, at: { start: number; query: string }, person: Person): { text: string; caret: number } {
  const end = at.start + 1 + at.query.length;
  const insert = `@${person.name} `;
  return { text: text.slice(0, at.start) + insert + text.slice(end), caret: at.start + insert.length };
}

/** Pure: the people picked whose "@Name" is still in the note when it's
 *  saved (deleting the name un-tags them). Each once. */
export function mentionedIds(text: string, picked: readonly Person[]): string[] {
  const out = new Set<string>();
  for (const p of picked) if (text.includes(`@${p.name}`)) out.add(p.id);
  return [...out];
}

/** Pure: split a saved note into plain text and "@Name" pieces so the names
 *  can be shown in bold. `names` = everyone who could be tagged. */
export function splitMentions(text: string, names: readonly string[]): Array<{ text: string; mention: boolean }> {
  const sorted = [...new Set(names)].filter(Boolean).sort((a, b) => b.length - a.length);
  if (sorted.length === 0) return [{ text, mention: false }];
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`@(?:${sorted.map(esc).join("|")})`, "g");
  const parts: Array<{ text: string; mention: boolean }> = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index! > last) parts.push({ text: text.slice(last, m.index), mention: false });
    parts.push({ text: m[0], mention: true });
    last = m.index! + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), mention: false });
  return parts;
}
